/**
 * Teleport detection for session trail rendering.
 *
 * Detects teleport jumps in transformed lat/lng coordinates so the renderer
 * can draw them as dashed lines instead of misleading straight lines across the map.
 *
 * Uses statistical outlier detection on inter-point Euclidean distances.
 */

/** Distance must exceed median * this factor to be flagged as a teleport */
const TELEPORT_MULTIPLIER = 50;

/** When median is near-zero, a jump covering >25% of the bounding box diagonal is a teleport */
const BBOX_FRACTION = 0.25;

/** Median values below this threshold trigger the bounding-box fallback */
const NEAR_ZERO = 1e-9;

/**
 * Represents a segment of a trail, either a walked path or a teleport jump.
 */
export interface TrailSegment {
    type: 'walk' | 'teleport';
    /** Index into the original coordinates array where this segment starts */
    startIndex: number;
    /** Index into the original coordinates array where this segment ends */
    endIndex: number;
    /** The coordinate points for this segment */
    coordinates: [number, number][];
}

export function euclidean(a: [number, number], b: [number, number]): number {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Compute the median of a numeric array.
 * Returns 0 for empty arrays.
 */
function median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
        return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
    }
    return sorted[mid] ?? 0;
}

/**
 * Compute the teleport distance threshold from inter-point distances.
 * Uses median * multiplier, falling back to bounding-box diagonal when the
 * median is near zero (stationary player).
 */
function computeThreshold(distances: number[], bboxDiagonal: number): number {
    const med = median(distances);
    if (med > NEAR_ZERO) {
        return med * TELEPORT_MULTIPLIER;
    }
    return bboxDiagonal * BBOX_FRACTION;
}

function computeBboxDiagonal(coordinates: [number, number][]): number {
    if (coordinates.length === 0) return 0;

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const [x, y] of coordinates) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
    }

    return euclidean([minX, minY], [maxX, maxY]);
}

/**
 * Filter out loading screen junk from the beginning of the trail.
 *
 * If the first walk segment consists entirely of a single repeated coordinate
 * (the game spamming the same position while loading) followed by a teleport,
 * those points are loading screen artifacts. Drop the initial walk and the
 * following teleport so the trail starts at real gameplay.
 */
function filterLoadingJunk(segments: TrailSegment[]): TrailSegment[] {
    if (segments.length < 2) return segments;

    const first = segments[0];
    const second = segments[1];

    if (first && second && first.type === 'walk' && second.type === 'teleport') {
        const coords = first.coordinates;
        const anchor = coords[0];
        if (anchor && coords.every(([x, y]) => x === anchor[0] && y === anchor[1])) {
            let result = segments.slice(2);
            while (result.length > 0 && result[0]?.type === 'teleport') {
                result = result.slice(1);
            }
            return result;
        }
    }

    return segments;
}

/**
 * Compute inter-point distances and identify teleport indices.
 * Returns an array of booleans where true means the segment between
 * point[i] and point[i+1] is a teleport.
 */
function identifyTeleportSegments(coordinates: [number, number][]): boolean[] {
    const distances: number[] = [];
    for (let i = 0; i < coordinates.length - 1; i++) {
        const current = coordinates[i];
        const next = coordinates[i + 1];
        if (current && next) {
            distances.push(euclidean(current, next));
        } else {
            distances.push(0);
        }
    }

    const bboxDiagonal = computeBboxDiagonal(coordinates);
    const threshold = computeThreshold(distances, bboxDiagonal);

    return distances.map(d => d > threshold);
}

/**
 * Strip exact-origin coordinates that are loading/teleport artifacts.
 *
 * Many games place the player at (0,0) during loading screens or while
 * fast-travelling between locations. These aren't real positions and would
 * pollute teleport detection and trail rendering.
 *
 * Apply this upstream before passing coordinates to detectTeleports so that
 * segment indices align with the coordinate array used for rendering.
 */
export function filterOriginPoints(coordinates: [number, number][]): [number, number][] {
    return coordinates.filter(([x, y]) => x !== 0 || y !== 0);
}

/**
 * Detects teleport jumps in a trail of coordinates using statistical outlier detection.
 *
 * Algorithm:
 * 1. Compute Euclidean distances between all consecutive point pairs
 * 2. Calculate the median inter-point distance (robust to outliers)
 * 3. Flag any segment where distance > median * TELEPORT_MULTIPLIER as a teleport
 * 4. Fallback: if median is near-zero, compare against the trail's bounding box diagonal
 * 5. Filter out loading screen junk at the start of the trail
 *
 * NOTE: Apply filterOriginPoints() to coordinates before calling this function
 * to strip (0,0) loading artifacts. This ensures segment indices align with
 * the coordinate array used for rendering.
 *
 * @param coordinates Array of [lng, lat] coordinate pairs (already transformed)
 * @returns Array of trail segments labeled as 'walk' or 'teleport'
 */
export function detectTeleports(coordinates: [number, number][]): TrailSegment[] {
    if (coordinates.length === 0) return [];

    const firstCoord = coordinates[0];
    if (coordinates.length === 1 && firstCoord) {
        return [
            {
                type: 'walk',
                startIndex: 0,
                endIndex: 0,
                coordinates: [firstCoord],
            },
        ];
    }

    const teleportFlags = identifyTeleportSegments(coordinates);

    const segments: TrailSegment[] = [];
    let segStart = 0;

    for (let i = 0; i < teleportFlags.length; i++) {
        if (teleportFlags[i]) {
            const teleportStart = coordinates[i];
            const teleportEnd = coordinates[i + 1];

            if (segStart <= i) {
                segments.push({
                    type: 'walk',
                    startIndex: segStart,
                    endIndex: i,
                    coordinates: coordinates.slice(segStart, i + 1),
                });
            }

            if (teleportStart && teleportEnd) {
                segments.push({
                    type: 'teleport',
                    startIndex: i,
                    endIndex: i + 1,
                    coordinates: [teleportStart, teleportEnd],
                });
            }

            segStart = i + 1;
        }
    }

    if (segStart < coordinates.length) {
        segments.push({
            type: 'walk',
            startIndex: segStart,
            endIndex: coordinates.length - 1,
            coordinates: coordinates.slice(segStart),
        });
    }

    return filterLoadingJunk(segments);
}

/**
 * Extract the first and last walk coordinates from a set of segments,
 * falling back to the raw coordinate endpoints when no walk segments exist.
 */
export function getWalkBoundaryCoordinates(
    segments: TrailSegment[],
    fallbackCoords: [number, number][],
): { start: [number, number] | undefined; end: [number, number] | undefined } {
    const walkSegments = segments.filter(s => s.type === 'walk');
    const firstWalk = walkSegments[0];
    const lastWalk = walkSegments[walkSegments.length - 1];
    return {
        start: firstWalk?.coordinates[0] ?? fallbackCoords[0],
        end: lastWalk
            ? lastWalk.coordinates[lastWalk.coordinates.length - 1]
            : fallbackCoords[fallbackCoords.length - 1],
    };
}
